import { Button, Card, Center, HStack, Spinner, Stack, VStack } from '@chakra-ui/react';
import { OnlineOrderBranchSettingFragment } from '@client/graphql/online-order-branch-setting-fragment';
import { getFragmentData } from '@client/graphql/types';
import { HappyHourMode } from '@client/graphql/types/graphql';
import ApplicationErrorView from '@client/module/error/application-error-view';
import { useFocusedBranchContext } from '@packages/core/branch/focused-branch-context';
import { toaster } from '@packages/provider/module/chakra-ui/components/ui/toaster';
import { useTranslate } from '@tolgee/react';
import { useEffect, useState } from 'react';
import useOnlineOrderBranchSetting from '../online-order/setting/logic/use-online-order-branch-setting';
import HappyHourCreateModel from './components/happy-hour-create-model';
import HappyHourTable from './components/happy-hour-table';
import OnlineOrderDiscount from './components/online-order-discount';
import useChangeHappyHourMode from './logics/use-change-happy-hour-mode';
import useGetHappyHours from './logics/use-get-happy-hour';
import ChakraFeatureAlert from './components/chakra-feature-alert';

export default function OnlineOrderHappyHour() {
  const { t } = useTranslate();
  const branch = useFocusedBranchContext();
  const [changeMode, { loading: changeModeLoading, error: changeModeError }] = useChangeHappyHourMode();
  const {
    data: onlineOrderData,
    loading: onlineOrderLoading,
    error: configError,
  } = useOnlineOrderBranchSetting(branch.branch?.id ?? '');
  const {
    data: happyHourData,
    loading: happyHourLoading,
    error: happyHourError,
  } = useGetHappyHours(branch.branch?.id ?? '');

  const [mode, setMode] = useState<HappyHourMode>(HappyHourMode.Basic);

  useEffect(() => {
    const resolveData = getFragmentData(
      OnlineOrderBranchSettingFragment,
      onlineOrderData?.onlineOrderBranchConfiguration,
    );
    if (resolveData?.happyHourMode) {
      setMode(resolveData.happyHourMode);
    }
  }, [onlineOrderData]);

  const handleModeChange = (newMode: HappyHourMode) => {
    if (!branch.branch?.id) return;

    changeMode({
      variables: {
        id: branch.branch.id,
        mode: newMode,
      },
    })
      .then(() => {
        toaster.create({
          description: t('happy-hour.set-mode.alert.des', 'Set mode successful'),
          type: 'info',
        });
        setMode(newMode);
      })
      .catch(() => {
        //Do nothing
      });
  };

  if (onlineOrderLoading || happyHourLoading) {
    return (
      <Center>
        <Spinner />
      </Center>
    );
  }

  const onlineOrderBranchSettingData = getFragmentData(
    OnlineOrderBranchSettingFragment,
    onlineOrderData?.onlineOrderBranchConfiguration,
  );

  if (!onlineOrderBranchSettingData) {
    return null;
  }

  const error = configError ?? happyHourError ?? changeModeError;

  return (
    <>
      {mode == HappyHourMode.Advanced && <ChakraFeatureAlert />}
      <Card.Root maxW="5xl" rounded="lg" shadow={'xs'}>
        <Card.Header borderBottom="1px solid" borderColor="gray.200" py="5">
          <HStack justify="space-between" align="start">
            <VStack align="start" gap={'4'}>
              <Card.Title fontSize="2xl" fontWeight="bold" color="gray.800">
                {t('onlineOrder.happyHour.title', 'Happy Hour')}
              </Card.Title>
              <Stack alignItems={'center'} direction={{ base: 'column', lg: 'row' }}>
                <HStack gap={0} rounded={'md'} border="1px solid" borderColor={'gray.500'}>
                  <Button
                    size="sm"
                    variant={mode === HappyHourMode.Basic ? 'solid' : 'outline'}
                    p={'2'}
                    disabled={changeModeLoading}
                    bgColor={mode === HappyHourMode.Basic ? 'gray.800' : 'white'}
                    color={mode === HappyHourMode.Basic ? 'white' : 'black'}
                    onClick={() => {
                      handleModeChange(HappyHourMode.Basic);
                    }}
                  >
                    {t('happy-hour.basic-mode', 'Basic mode')}
                  </Button>
                  <Button
                    size="sm"
                    variant={mode === HappyHourMode.Advanced ? 'solid' : 'outline'}
                    bgColor={mode === HappyHourMode.Advanced ? 'gray.800' : 'white'}
                    color={mode === HappyHourMode.Advanced ? 'white' : 'black'}
                    disabled={changeModeLoading}
                    p="2"
                    onClick={() => {
                      handleModeChange(HappyHourMode.Advanced);
                    }}
                  >
                    {t('happy-hour.advance-mode', 'Advanced mode')}
                  </Button>
                </HStack>
                {changeModeLoading && <Spinner color="blue.500" borderWidth="3px" size={'md'} />}
              </Stack>
            </VStack>

            {mode === HappyHourMode.Advanced && <HappyHourCreateModel />}
          </HStack>
        </Card.Header>
        <Card.Body p={0}>
          <ApplicationErrorView error={error} />
          {mode === HappyHourMode.Basic ? (
            <OnlineOrderDiscount onlineOrderBranchSettingData={onlineOrderBranchSettingData} />
          ) : (
            <HappyHourTable data={happyHourData} />
          )}
        </Card.Body>
      </Card.Root>
    </>
  );
}
